// Generate the asset path
export function asset(path) {
    const baseUrl = window.App.assetUrl ?? window.location.origin;

    return `${baseUrl.replace(/\/$/, '')}/${path.replace(/^\/|\/$/g, '')}`;
}

// Perform a deep clone of the target object
export function cloneDeep(target) {
    return window.structuredClone ? window.structuredClone(target) : window.JSON.parse(window.JSON.stringify(target));
}

// Copy the provided text to the clipboard
export function copyToClipboard(text, target = null, options = {}) {
    options = Object.assign(
        {
            container: 'body',
            delay: 1100,
            placement: 'left',
        },
        options
    );

    const showTooltip = (title) => {
        if (!target) return;

        // Set text selection if applicable
        if (typeof target.select !== 'undefined') {
            target.focus();
            target.select();
        }

        // Show tooltip
        const config = {
            title: title,
            container: options.container,
            placement: options.placement,
            trigger: 'manual',
        };

        if (IS_BOOTSTRAP4) {
            const targetJq = $(target);

            if (targetJq.data('bs.tooltip')) return;

            targetJq
                .tooltip(config)
                .on('hidden.bs.tooltip', () => targetJq.tooltip('dispose'))
                .tooltip('show');

            window.setTimeout(() => targetJq.tooltip('hide'), options.delay);
        } else if (IS_BOOTSTRAP5) {
            const tooltip = new bootstrap.Tooltip(target, config);

            target.addEventListener('hidden.bs.tooltip', () => (tooltip._element ? tooltip.dispose() : null));

            tooltip.show();

            window.setTimeout(() => (tooltip._element ? tooltip.hide() : null), options.delay);
        }
    };

    const copiedText = __('Copied') + '!';
    const errorText = __('Error') + '!';

    if (navigator.clipboard) {
        navigator.clipboard
            .writeText(text)
            .then(() => showTooltip(copiedText))
            .catch(() => showTooltip(errorText));
    } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;

        mergeStyles(textarea, {
            position: 'fixed',
            top: '-100px',
            left: '-100px',
            height: 0,
            width: 0,
        });

        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();

        try {
            if (document.execCommand('copy')) {
                showTooltip(copiedText);
            } else {
                showTooltip(errorText);
            }
        } catch {
            showTooltip(errorText);
        }

        document.body.removeChild(textarea);
    }
}

// Create an enum by wrapping an object in a proxy
// https://dmitripavlutin.com/javascript-enum/#4-enum-based-on-a-proxy
export function createEnum(original) {
    const pluck = (obj, type) =>
        Object.entries(obj)
            .filter(([name]) => /[A-Z]/.test(name.charAt(0)))
            .map(([name, value]) => (type === 'name' ? name : value));

    original.names = pluck(original, 'name');
    original.values = pluck(original, 'value');

    return new Proxy(original, {
        get(target, name) {
            if (!Object.hasOwn(target, name)) {
                throw new Error(`"${name}" does not exist in the enum`);
            }

            return target[name];
        },
        set() {
            throw new Error('Enum is immutable');
        },
    });
}

// Get or set CSS variable value
export function cssVar(name, value = null) {
    if (value) {
        document.documentElement.style.setProperty(name, value);

        return value;
    }

    return window.getComputedStyle(document.documentElement).getPropertyValue(name);
}

// Shortcut for dispatching a custom event
export function dispatchCustomEvent(eventName, detail = {}, target = document, options = {}) {
    return target.dispatchEvent(new CustomEvent(eventName, Object.assign({ detail }, options)));
}

// Escape HTML string
// Based on listed sources, with slight modifications
// Source #1: http://css-tricks.com/snippets/javascript/htmlentities-for-javascript/
// Source #2: http://stackoverflow.com/a/7124052
export function escapeHtml(string) {
    return String(string).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/'/g, '&#039;').replace(/"/g, '&quot;');
}

// Escape regular expression string
// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
export function escapeRegExp(string) {
    return String(string).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Get Font Awesome icon class based on the file extension
export const fileIcons = Object.fromEntries(
    [
        ['fa-file-image', ['bmp', 'gif', 'jpg', 'jpeg', 'png', 'webp', 'svg', 'ico', 'tif', 'tiff']],
        ['fa-file-word', ['doc', 'docx', 'odt', 'rtf']],
        ['fa-file-excel', ['xls', 'xlsx', 'xlsm', 'ods', 'csv']],
        ['fa-file-powerpoint', ['ppt', 'pptx', 'odp']],
        ['fa-file-pdf', ['pdf']],
        ['fa-file-audio', ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac']],
        ['fa-file-video', ['mp4', 'avi', 'mov', 'mkv', 'webm', 'wmv', 'm4v']],
        ['fa-file-zipper', ['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz']],
    ].flatMap(([icon, extensions]) => extensions.map((extension) => [extension, icon]))
);

export function fileIcon(path) {
    const extension = path.split('.').pop()?.toLowerCase();

    return fileIcons[extension] ?? 'fa-file';
}

// Render file(s) in suitable medium
export function fileViewer(title, files, config = {}) {
    if (!Array.isArray(files)) {
        files = [files];
    }

    const isMobileView = isMobile();
    const supportedTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx', 'mp3', 'wav', 'mp4', 'webm'];
    let preloadImageSource;

    const createContent = (url, downloadUrl = null, type = null, counter = 1) => {
        downloadUrl = downloadUrl ?? url;
        url = new URL(url);

        if (type === null) {
            type = url.pathname.toLowerCase().split('.').pop();

            if (!supportedTypes.includes(type)) {
                type = null;
            }
        }

        let content;
        const height = isMobileView ? 450 : 500;
        const loadingMode = counter > 1 ? 'lazy' : 'eager';

        if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(type)) {
            content = `<img src="${url}" class="img-fluid border border-dark mx-auto mb-0" loading="${loadingMode}">`;
            preloadImageSource = preloadImageSource ?? url;
        } else if (type === 'pdf') {
            if (isMobileView) {
                url = zroute('/pdf-viewer', { url });
            }

            content = `<iframe class="w-100 mb-0 border border-dark" src="${url}" loading="${loadingMode}" style="height: ${height}px"></iframe>`;
        } else if (['doc', 'docx'].includes(type)) {
            content = `<iframe class="w-100 mb-0 border border-dark" src="https://view.officeapps.live.com/op/embed.aspx?src=${url}" loading="${loadingMode}" style="height: ${height}px"></iframe>`;
        } else if (['mp3', 'wav'].includes(type)) {
            content = `<audio class="w-100" src="${url}" controls ></audio>`;
        } else if (['mp4', 'webm'].includes(type)) {
            content = `<video class="w-100" src="${url}" controls ></video>`;
        } else {
            content = `<a href="${url}" target="_blank">${url}</a>`;
        }

        content += `<a class="d-none download-url" href="${downloadUrl}" target="_blank" download></a>`;

        return content;
    };

    const message = ((files) => {
        if (files.length === 1) {
            return createContent(files[0].url, files[0]?.downloadUrl, files[0]?.type);
        }

        const tabNavs = [];
        const tabPanes = [];
        let counter = 1;

        for (const { name, url, downloadUrl = null, type = null } of files) {
            tabNavs.push(
                `<button type="button" class="nav-link ${counter > 1 ? '' : 'active'}" data-${IS_BOOTSTRAP5 ? 'bs-' : ''}toggle="tab" data-${IS_BOOTSTRAP5 ? 'bs-' : ''}target="#file-tab-${counter}" role="tab">${name ?? 'Tab #' + counter}</button>`
            );

            tabPanes.push(
                `<div class="tab-pane ${counter > 1 ? '' : 'active'}" id="file-tab-${counter}" role="tabpanel">${createContent(url,downloadUrl,type,counter)}</div>`
            );

            counter++;
        }

        return `<div class="nav nav-tabs mb-3" role="tablist">${tabNavs.join('')}</div><div class="tab-content">${tabPanes.join('')}</div>`;
    })(files);

    const showModal = () => {
        abmodal({
            title,
            message,
            backdrop: config.backdrop ?? true,
            centered: config.centered ?? true,
            closeButton: config.closeButton ?? true,
            onEscape: config.onEscape ?? true,
            size: config.size ?? 'extra-large',
            buttons: {
                download: {
                    label: `<span class="fas fa-download ${IS_BOOTSTRAP5 ? 'me-1' : 'mr-1'}"></span>${__('Download')}`,
                    className: 'btn-outline-dark text-capitalize',
                    callback() {
                        this.$root.querySelector(`${files.length > 1 ? '.tab-pane.active' : ''} .download-url`)?.click();
                    },
                },
                close: {
                    label: __('Close'),
                },
            },
        });
    };

    if (preloadImageSource) {
        const image = new Image();

        new Promise((resolve) => (image.onload = resolve)).then(() => showModal());

        image.src = preloadImageSource;
    } else {
        showModal();
    }
}

// Format a value with suitable unit suffix
export function formatBytes(value, decimals = 0) {
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let index = 0;

    while (value >= 1024 && index < units.length - 1) {
        value /= 1024;
        index++;
    }

    return formatDecimal(value, decimals) + ' ' + units[index];
}

// Simple date formatter
// Use PHP format, but not all formats are supported
export function formatDate(date, format = 'c') {
    date = new Date(date);

    if (isNaN(date.valueOf())) {
        return '';
    }

    const days = [__('Sunday'), __('Monday'), __('Tuesday'), __('Wednesday'), __('Thursday'), __('Friday'), __('Saturday')];

    const months = [
        __('January'),
        __('February'),
        __('March'),
        __('April'),
        __('May'),
        __('June'),
        __('July'),
        __('August'),
        __('September'),
        __('October'),
        __('November'),
        __('December'),
    ];

    // Determine leap year
    // https://stackoverflow.com/a/16353241
    const isLeapYear = (year) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

    // Prepare data
    let milliseconds = date.getMilliseconds();
    let seconds = date.getSeconds();
    let minutes = date.getMinutes();
    let hours24 = date.getHours();
    let hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
    let timePeriod = hours24 < 12 ? 'AM' : 'PM';
    let dayOfWeek = date.getDay();
    let isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
    let dayOfMonth = date.getDate();
    let month = date.getMonth();
    let fullYear = date.getFullYear();
    let shortYear = String(fullYear).substring(2);
    let monthDurations = [31, isLeapYear(fullYear) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Calculate day of year
    let dayOfYear = month === 0 ? dayOfMonth - 1 : monthDurations.slice(0, month).reduce((carry, value) => carry + value, dayOfMonth - 1);

    // Calculate week number
    let firstDayOfYear = new Date(date.getFullYear() + '-01-01 00:00:00').getDay();
    let lastDayOfYear = new Date(date.getFullYear() + '-12-31 00:00:00').getDay();
    let firstWeekOfYear = 1;

    if (firstDayOfYear === 5) {
        firstWeekOfYear = 53;
    } else if (firstDayOfYear === 6) {
        firstWeekOfYear = isLeapYear(fullYear - 1) ? 53 : 52;
    } else if (firstDayOfYear === 0) {
        firstWeekOfYear = 52;
    }

    let weekNumber = Math.floor((dayOfYear + firstDayOfYear - 1) / 7);

    if (firstWeekOfYear === 1) {
        weekNumber = weekNumber + 1;
    } else {
        weekNumber = weekNumber === 0 ? firstWeekOfYear : weekNumber;
    }

    if (month === 11 && dayOfMonth >= 28) {
        if ([1, 2, 3].includes(lastDayOfYear)) {
            weekNumber = 1;
        } else if (lastDayOfYear === 4) {
            weekNumber = 53;
        } else if (lastDayOfYear === 5) {
            weekNumber = isLeapYear(fullYear) ? 53 : 52;
        } else {
            weekNumber = 52;
        }
    }

    // Calculate timezone offset
    let offset = date.getTimezoneOffset();
    let isUTC = Math.abs(offset) === 0;
    let offsetParts = String(Math.abs(offset) / 60).split('.');
    let offsetSign = Math.sign(offset) > 0 ? '-' : '+';
    let offsetHour = leftPad(offsetParts[0], 0, 2);
    let offsetMinute = leftPad(offsetParts[1] ?? 0, 0, 2);

    // Prepare format characters
    const characters = {
        v: milliseconds,
        s: leftPad(seconds, 0, 2),
        i: leftPad(minutes, 0, 2),
        g: hours12,
        G: hours24,
        h: leftPad(hours12, 0, 2),
        H: leftPad(hours24, 0, 2),
        a: timePeriod.toLowerCase(),
        A: timePeriod,
        w: dayOfWeek,
        N: isoDayOfWeek,
        l: days[dayOfWeek],
        D: days[dayOfWeek].substring(0, 3),
        j: dayOfMonth,
        d: leftPad(dayOfMonth, 0, 2),
        z: dayOfYear,
        W: leftPad(weekNumber, 0, 2),
        n: month + 1,
        m: leftPad(month + 1, 0, 2),
        F: months[month],
        M: months[month].substring(0, 3),
        t: monthDurations[month],
        y: shortYear,
        Y: fullYear,
        L: isLeapYear(fullYear) ? 1 : 0,
        O: offsetSign + offsetHour + offsetMinute,
        P: offsetSign + offsetHour + ':' + offsetMinute,
        Z: offset * -60,
        U: Math.trunc(date.getTime() / 1000),
    };

    // Preprocess
    format = format
        .replace('p', isUTC ? 'Z' : 'P')
        .replace('\\c', '\\#99')
        .replace('c', 'Y-m-dTH:i:sP')
        .replace('\\#99', '\\c')
        .replace('\\r', '\\#114')
        .replace('r', 'D, j M Y H:i:s O')
        .replace('\\#114', '\\r');

    // Process format string
    let result = '';

    for (let i = 0; i < format.length; i++) {
        const previousChar = format.charAt(i - 1);
        const currentChar = format.charAt(i);

        if (typeof characters[currentChar] !== 'undefined' && previousChar !== '\\') {
            result += characters[currentChar];
        } else if (currentChar !== '\\') {
            result += currentChar;
        }
    }

    return result;
}

// Format decimal number for display purpose
export function formatDecimal(number, minDecimals = 0, maxDecimals = minDecimals) {
    if (minDecimals > maxDecimals) {
        throw new Error(`"maxDecimals" must be greater than or equal to "minDecimals"`);
    }

    return Number(number).toLocaleString(undefined, {
        minimumFractionDigits: minDecimals,
        maximumFractionDigits: maxDecimals,
    });
}

// Get Alpine component by x-title or id attribute
export function getAlpineComponent(id) {
    return getAlpineData(`[x-title="${id}"], #${id}`);
}

// Get the closest Alpine data for an element
export function getAlpineData(selector, determinant = 0) {
    const element = selector instanceof Element ? selector : document.querySelector(selector);

    if (element === null) {
        return null;
    }

    const dataStack = Alpine.closestDataStack(element);

    if (dataStack.length === 0) {
        return null;
    }

    if (typeof determinant === 'number' && dataStack[determinant]) {
        return dataStack[determinant];
    } else if (typeof determinant === 'function') {
        for (const data of dataStack) {
            if (determinant(data)) {
                return data;
            }
        }
    }

    return null;
}

// Get Font Awesome icon class based on the file extension
export function getFileIconClass(filename, defaultValue = '') {
    return (
        {
            pdf: 'fa-file-pdf',
            doc: 'fa-file-word',
            docx: 'fa-file-word',
            bmp: 'fa-file-image',
            gif: 'fa-file-image',
            jpg: 'fa-file-image',
            jpeg: 'fa-file-image',
            png: 'fa-file-image',
            webp: 'fa-file-image',
        }[filename.split('.').pop().toLowerCase()] ?? defaultValue
    );
}

// Get HTML contents of an element
export function getHtml(selector) {
    return (selector instanceof Element ? selector : document.querySelector(selector)).innerHTML;
}

// Get all search parameters of the current page
export function getSearchParams(defaultParams = {}) {
    const params = {};

    for (const [key, value] of new URLSearchParams(window.location.search).entries()) {
        params[key] = value;
    }

    return Object.assign({}, defaultParams, params);
}

// Get the timezone from the browser
// https://stackoverflow.com/questions/1091372/getting-the-clients-time-zone-and-offset-in-javascript/34602679#34602679
export function getTimezone(defaultTimezone = 'UTC') {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone ?? defaultTimezone;
    } catch {
        return defaultTimezone;
    }
}

// Get unique CSS selector for the provided element
export function getUniqueCssSelector(element) {
    if (element.id) {
        return `#${CSS.escape(element.id)}`;
    }

    let selector = element.tagName.toLowerCase();
    const classSelector = element.classList.length > 0 ? `.${CSS.escape(element.classList.item(0))}` : '';
    selector += classSelector;

    if (!element.parentElement) {
        return selector;
    }

    const parent = element.parentElement;
    const index = Array.from(parent.children).indexOf(element) + 1;
    selector = `${selector}:nth-child(${index})`;

    if (document.querySelectorAll(selector).length === 1) {
        return selector;
    } else {
        selector = selector.replace(classSelector, '');
    }

    return `${parent.tagName !== 'BODY' && parent.parentElement ? getUniqueCssSelector(parent) : parent.tagName.toLowerCase()} > ${selector}`;
}

// Check element's visibility
export function isElementVisible(element) {
    return !!(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
}

// Check element is in viewport
export function isElementInViewport(element) {
    const rect = element.getBoundingClientRect();

    return (
        rect.bottom >= 0 &&
        rect.right >= 0 &&
        rect.top <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.left <= (window.innerWidth || document.documentElement.clientWidth)
    );
}

// Check whether the browser is in mobile or dekstop
// Simple check, based on https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#os
// Combined with browser's width check as well
export function isMobile() {
    return /mobi/i.test(navigator.userAgent) && window.innerWidth < 576;
}

// Pad the string from left with the provided character
export function leftPad(string, character, length) {
    string = String(string);
    character = String(character);

    const count = length - string.length;

    if (count > 0) {
        string = character.repeat(count) + string;
    }

    return string;
}

// Merge Alpine components, where init() and destroy() from provided components are grouped and run consecutively
export function mergeAlpineComponents(...components) {
    const data = {
        inits: [],
        destroys: [],
    };

    for (const component of components) {
        for (const type of ['init', 'destroy']) {
            if (Object.hasOwn(component, type)) {
                data[type + 's'].push(component[type]);
                delete component[type];
            }
        }
    }

    components.push({
        init() {
            data.inits.forEach((init) => init.call(this));
        },
        destroy() {
            data.destroys.forEach((destroy) => destroy.call(this));
        },
    });

    return mergeObjects.apply(Helper, components);
}

// Merge objects, like Object.assign, but preserve getters and setters
export function mergeObjects(target, ...sources) {
    for (const source of sources) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    }

    return target;
}

// Merge element's style with the provided styles
export function mergeStyles(element, styles) {
    for (const [key, value] of Object.entries(styles)) {
        element.style[key] = value;
    }
}

// Minify HTML string
// Can be used as a tag function for template literal
export function minifyHtml(strings, ...values) {
    let result = '';

    if (Array.isArray(strings)) {
        if (strings.length === 1) {
            result = strings[0];
        } else {
            for (let i = 0; i < strings.length - 1; i++) {
                result += strings[i] + values[i];
            }

            result += strings[strings.length - 1];
        }
    } else {
        result = strings;
    }

    const pres = result.match(/<pre([\s\S]*?)<\/pre>/g) ?? [];

    for (let i = 0; i < pres.length; i++) {
        result = result.replace(pres[i], `_PRE_BLOCK_${i}_`);
    }

    result = result.replace(/^\s+/gm, '').replace(/\s+$/gm, '').replace(/\n/g, ' ');

    for (let i = 0; i < pres.length; i++) {
        result = result.replace(`_PRE_BLOCK_${i}_`, pres[i]);
    }

    return result;
}

// Parse dataset from the element with proper casts
export function parseElementDataset(element, defaultData = {}) {
    const data = Object.assign({}, defaultData);

    Object.entries(element.dataset).forEach(([name, value]) => {
        if (!name.includes('.')) {
            data[name] = value;

            return;
        }

        const [key, cast = 'string'] = name.split('.');

        if (cast === 'int') {
            value = parseInt(value, 10);
        } else if (cast === 'float') {
            value = parseFloat(value, 10);
        } else if (cast === 'bool') {
            if (/^\d+$/.test(value)) {
                value = Boolean(parseInt(value));
            } else if (value === 'true' || value === 'false') {
                value = JSON.parse(value);
            } else {
                value = Boolean(value);
            }
        } else if (cast === 'array' || cast === 'object') {
            value = JSON.parse(value);
        } else if (cast === 'null') {
            value = null;
        } else if (cast === 'undefined') {
            value = undefined;
        }

        data[key] = value;
    });

    return data;
}

// Render an array as an HTML table
export function renderArrayAsHtmlTable(array, level = 1) {
    const createContainer = (contents) => `<div class="table-array">${contents}</div>`;

    if (level <= 0) {
        return createContainer(renderValueAsColorizedJson(array));
    }

    const createRow = (key, value, type = 'string') => `<tr><th>${key}</th><td class="${type}">${value}</td></tr>`;
    const rows = [];

    for (let [key, value] of Object.entries(array)) {
        if (typeof value === 'object' && value !== null) {
            if (level - 1 > 0) {
                value = renderArrayAsHtmlTable(value, level - 1);
            } else {
                value = renderValueAsColorizedJson(value);
            }

            rows.push(createRow(key, value));
        } else {
            let type;

            if (value === null || value === undefined) {
                type = 'null';
                value = String(value);
            } else if (typeof value === 'boolean') {
                value = String(value);
                type = value;
            } else if (typeof value === 'number') {
                type = 'number';
            } else {
                type = 'string';
            }

            rows.push(createRow(escapeHtml(key), escapeHtml(value), type));
        }
    }

    return createContainer(`<table class="table"><tbody>${rows.join('')}</tbody></table>`);
}

// Render a value as a colorized JSON string in HTML
export function renderValueAsColorizedJson(value, depth = 0) {
    const createSpan = (value, type) => `<span class="${type}">${value}</span>`;

    let result = '';

    if (typeof value === 'object' && value !== null) {
        const isList = Array.isArray(value);
        const openBracket = isList ? '[' : '{';
        const closeBracket = isList ? ']' : '}';

        result = createSpan(openBracket, 'bracket') + '\n';
        value = Object.entries(value);

        let counter = 0;

        for (const [key, item] of value) {
            counter++;
            result += ' '.repeat((depth + 1) * 4);
            result += isList ? '' : createSpan(JSON.stringify(key), 'key') + ': ';
            result += renderValueAsColorizedJson(item, depth + 1);
            result += counter < value.length ? ',' : '';
            result += '\n';
        }

        result += ' '.repeat(depth * 4) + createSpan(closeBracket, 'bracket');
    } else if (typeof value === 'number') {
        result = createSpan(value, 'number');
    } else if (typeof value === 'boolean') {
        result = createSpan((value = String(value)), value);
    } else if (value === null || value === undefined) {
        result = createSpan(String(value), 'null');
    } else {
        result = createSpan(escapeHtml(JSON.stringify(value)), 'string');
    }

    if (depth === 0) {
        result = `<pre class="colorized-json">${result}</pre>`;
    }

    return result;
}

// Remove full page spinner
export function removePageSpinner() {
    document.querySelectorAll('.modal-dialog').forEach((modalDialog) => (modalDialog.style.filter = ''));

    document.body.classList.remove('show-page-spinner');
    document.getElementById('page-spinner-container')?.remove();
}

// Scroll to an element
export function scrollToElement(element, duration = 0, offset = 0, callback = null, container = window) {
    if (duration < 10) {
        element.scrollIntoView();
        if (callback) callback(element);
        return;
    }

    let elementTop = element.getBoundingClientRect().top;
    let startPos = typeof container.scrollY === 'number' ? container.scrollY : container.scrollTop;
    let endPos = startPos + elementTop - offset;
    let goUp = endPos < startPos;
    let distance = Math.abs(endPos - startPos);
    let step = distance / duration;
    let startTime = 0;

    const scrollAnimation = (timestamp) => {
        startTime = startTime || timestamp;
        let elapsed = timestamp - startTime;
        let currentPos = startPos + (goUp ? -1 : 1) * (step * elapsed);

        currentPos = (goUp ? currentPos < endPos : currentPos > endPos) ? endPos : currentPos;

        container.scrollTo(0, currentPos);

        if (elapsed >= duration) {
            if (callback) callback(element);
            return;
        }

        window.requestAnimationFrame(scrollAnimation);
    };

    window.requestAnimationFrame(scrollAnimation);
}

// Show a toast message
export function showNotification(type = 'primary', title = '', message = '', delay = 1500) {
    const id = 'toast-' + Date.now();

    let container = document.getElementById('toast-container');

    if (!container) {
        container = document.createRange().createContextualFragment('<div id="toast-container"></div>').firstElementChild;
        document.body.prepend(container);
    }

    let closeButton;

    if (IS_BOOTSTRAP4) {
        closeButton = '<button type="button" class="close text-white" data-dismiss="toast" aria-label="Close"><span aria-hidden="true">&times;</span></button>';
    } else if (IS_BOOTSTRAP5) {
        closeButton = '<button type="button" class="btn-close btn-close-white" data-bs-dismiss="toast" aria-label="Close"></button>';
    }

    const element = document.createRange().createContextualFragment(
        `<div class="toast" id="${id}" role="alert" aria-live="assertive" aria-atomic="true"><div class="toast-header bg-${type} text-white"><span class="toast-title">${title}</span>${closeButton}</div><div class="toast-body">${message}</div><div class="progress"><div class="progress-bar bg-${type}" role="progressbar" style="animation-duration: ${delay}ms"></div></div></div>`
    ).firstElementChild;

    container.append(element);

    let endListener;

    if (IS_BOOTSTRAP4) {
        $(element)
            .toast({ autohide: false })
            .on('hidden.bs.toast', () => $(element).toast('dispose').remove())
            .toast('show');

        endListener = () => $(element).toast('hide');
    } else if (IS_BOOTSTRAP5) {
        const toast = new bootstrap.Toast(element, { autohide: false });

        element.addEventListener('hidden.bs.toast', () => {
            toast._element ? toast.dispose() : null;
            element.remove();
        });

        toast.show();

        endListener = () => (toast._element ? toast.hide() : null);
    }

    element.querySelector('.progress-bar').addEventListener('animationend', endListener);

    return element;
}

// Show an error toast message
export function showNotificationError(message, delay) {
    return showNotification('danger', __('Error'), message, delay);
}

// Show an info toast message
export function showNotificationInfo(message, delay) {
    return showNotification('info', __('Info'), message, delay);
}

// Show a success toast message
export function showNotificationSuccess(message, delay) {
    return showNotification('success', __('Successful'), message, delay);
}

// Show a warning toast message
export function showNotificationWarning(message, delay) {
    return showNotification('warning', __('Warning'), message, delay);
}

// Show full page spinner
export function showPageSpinner(message) {
    const modalDialogs = document.querySelectorAll('.modal-dialog');
    const zIndex = 1050 + modalDialogs.length * 10;

    modalDialogs.forEach((modalDialog) => (modalDialog.style.filter = 'blur(1px)'));

    document.body.classList.add('show-page-spinner');
    document.body.querySelector('#page-spinner-container')?.remove();
    document.body.insertAdjacentHTML(
        'beforeend',
        `<div id="page-spinner-container" style="z-index: ${zIndex}">${message ? `<div class="spinner-message">${message}</div>` : ''}<div class="spinner-border"></div></div>`
    );
}

// Strip http:// or https:// from the beginning of the given URL
export function stripHttp(url) {
    return url.replace(/^https?:\/\//, '');
}

// Generate the versioned asset path
export function versa(path) {
    path = String(path).replace(/^\/|\/$/g, '');

    if (path === '' || path.includes('?h=') || path.includes('&h=')) {
        return asset(path);
    }

    const { strategy, hashes } = window._versionedAssets ?? { strategy: 'query-string', hashes: {} };
    const hash = hashes[path] ?? null;

    if (!hash) {
        return asset(path);
    }

    const extensionPosition = path.lastIndexOf('.');
    const prefix = path.slice(0, extensionPosition);
    const extension = path.slice(extensionPosition + 1);

    return asset(strategy === 'filename' ? `${prefix}.h${hash}.${extension}` : `${prefix}.${extension}?h=${hash}`);
}

// Helper object
export default Object.freeze({
    asset,
    cloneDeep,
    copyToClipboard,
    createEnum,
    cssVar,
    dispatchCustomEvent,
    escapeHtml,
    escapeRegExp,
    fileIcon,
    fileViewer,
    formatBytes,
    formatDate,
    formatDecimal,
    getAlpineComponent,
    getAlpineData,
    getFileIconClass,
    getHtml,
    getSearchParams,
    getTimezone,
    getUniqueCssSelector,
    isElementVisible,
    isElementInViewport,
    isMobile,
    leftPad,
    mergeAlpineComponents,
    mergeObjects,
    mergeStyles,
    minifyHtml,
    parseElementDataset,
    renderArrayAsHtmlTable,
    renderValueAsColorizedJson,
    removePageSpinner,
    scrollToElement,
    showNotification,
    showNotificationError,
    showNotificationInfo,
    showNotificationSuccess,
    showNotificationWarning,
    showPageSpinner,
    stripHttp,
    versa,
});
